/**
 * Configuration for the DAM "Advanced Search" panel.
 *
 * Every data point maps 1:1 onto a `field` understood by the backend
 * `buildCondition(...)` switch, and each `valueType` decides which control the
 * panel renders and how the value is serialised into the `advancedFilters`
 * payload sent to `/apidam/search`.
 */

export const OPERATORS = {
  EQUALS: 'equal to',
  NOT_EQUALS: 'not equal to',
  CONTAINS: 'contains',
  STARTS_WITH: 'starts with',
  GREATER_THAN: 'greater than',
  LESS_THAN: 'less than',
};

export const JOIN_OPTIONS = [
  { value: 'AND', label: 'AND' },
  { value: 'OR', label: 'OR' },
];

export const JOIN_HINTS = {
  AND: 'All the Search Criteria must match to show results',
  OR: 'Either one of the Search Criteria will show matching results',
};

const TEXT_OPERATORS = ['CONTAINS', 'EQUALS', 'NOT_EQUALS', 'STARTS_WITH'];
const NUMBER_OPERATORS = ['EQUALS', 'NOT_EQUALS', 'GREATER_THAN', 'LESS_THAN'];
const LIST_OPERATORS = ['EQUALS', 'NOT_EQUALS', 'CONTAINS'];
const DATE_OPERATORS = ['EQUALS', 'GREATER_THAN', 'LESS_THAN'];
const BOOLEAN_OPERATORS = ['EQUALS', 'NOT_EQUALS'];
// One user per condition, so there is no list to match against.
const USER_OPERATORS = ['EQUALS', 'NOT_EQUALS'];

/**
 * valueType
 *  text      -> free text input
 *  number    -> numeric input
 *  boolean   -> Yes / No single select
 *  date      -> date input, serialised as YYYY-MM-DD
 *  options   -> single select fed by `optionsKey`, serialised as a scalar
 *  multi     -> multi select (chips) fed by `optionsKey`, serialised as an array

 * Either select loads from the API instead when its source supplies `loadOptions`.
 *
 * cast
 *  Mirrors the reference payload the API was verified against: the numeric
 *  dimension/size fields go over the wire as strings, `faces` as a number.
 */
export const DATA_POINTS = [
  {
    field: 'assetName',
    label: 'File Name',
    valueType: 'text',
    operators: TEXT_OPERATORS,
  },
  {
    field: 'titleName',
    label: 'Display Name',
    valueType: 'text',
    operators: TEXT_OPERATORS,
  },
  {
    field: 'assetId',
    label: 'Asset ID',
    valueType: 'text',
    operators: ['EQUALS', 'NOT_EQUALS', 'CONTAINS'],
  },
  {
    field: 'fileType',
    label: 'File Type',
    valueType: 'options',
    optionsKey: 'fileTypes',
    operators: ['EQUALS', 'NOT_EQUALS', 'CONTAINS'],
  },
  {
    field: 'fileExtension',
    label: 'File Extension',
    valueType: 'options',
    optionsKey: 'fileExtensions',
    isCreatable: true,
    operators: ['EQUALS', 'NOT_EQUALS', 'CONTAINS'],
  },
  {
    field: 'fileOrientation',
    label: 'Orientation',
    valueType: 'options',
    optionsKey: 'orientations',
    operators: ['EQUALS', 'NOT_EQUALS', 'CONTAINS'],
  },
  {
    field: 'fileSize',
    label: 'File Size (MB)',
    valueType: 'number',
    cast: 'string',
    operators: NUMBER_OPERATORS,
  },
  {
    field: 'fileWidth',
    label: 'Width (px)',
    valueType: 'number',
    cast: 'string',
    operators: NUMBER_OPERATORS,
  },
  {
    field: 'fileHeight',
    label: 'Height (px)',
    valueType: 'number',
    cast: 'string',
    operators: NUMBER_OPERATORS,
  },
  {
    field: 'faces',
    label: 'Number of Faces',
    valueType: 'number',
    cast: 'number',
    operators: NUMBER_OPERATORS,
  },
  {
    field: 'transparent',
    label: 'Transparent',
    valueType: 'boolean',
    operators: BOOLEAN_OPERATORS,
  },
  {
    field: 'createdAt',
    label: 'Created On',
    valueType: 'date',
    operators: DATE_OPERATORS,
  },
  {
    // Value is a user id, taken from the client user list API. Pick one user
    // per condition; several users means several conditions joined with OR.
    field: 'createdBy',
    label: 'Created By',
    valueType: 'options',
    optionsKey: 'users',
    operators: USER_OPERATORS,
  },
  // {
  //   // Value is a user id, taken from the client user list API.
  //   field: 'sharedWith',
  //   label: 'Shared With',
  //   valueType: 'options',
  //   optionsKey: 'users',
  //   operators: USER_OPERATORS,
  // },
  {
    field: 'fileDescription',
    label: 'Description',
    valueType: 'text',
    operators: TEXT_OPERATORS,
  },
  {
    field: 'skuCode',
    label: 'Linked SKU Codes',
    valueType: 'multi',
    optionsKey: 'skuCodes',
    isCreatable: true,
    operators: LIST_OPERATORS,
  },
  {
    field: 'updatedAt',
    label: 'Last Changed On',
    valueType: 'date',
    operators: DATE_OPERATORS,
  },
  {
    // Value is a user id, taken from the client user list API. Pick one user
    // per condition; several users means several conditions joined with OR.
    field: 'updatedBy',
    label: 'Last Changed By',
    valueType: 'options',
    optionsKey: 'users',
    operators: USER_OPERATORS,
  },
];

export const DATA_POINT_OPTIONS = DATA_POINTS.map((item) => ({
  value: item.field,
  label: item.label,
}));

export const getDataPoint = (field) => DATA_POINTS.find((item) => item.field === field);

export const getOperatorOptions = (field) => {
  const dataPoint = getDataPoint(field);
  const keys = dataPoint?.operators || Object.keys(OPERATORS);
  return keys.map((key) => ({ value: key, label: OPERATORS[key] }));
};

let rowSequence = 0;

export const createEmptyRow = (join = 'AND') => ({
  id: `advanced-row-${(rowSequence += 1)}`,
  field: '',
  operator: '',
  value: null,
  join,
});

/** A row only reaches the payload once it carries a field, an operator and a value. */
export const isRowComplete = (row) => {
  if (!row?.field || !row?.operator) return false;

  const { value } = row;

  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === 'boolean') return true;
  if (typeof value === 'number') return !Number.isNaN(value);

  return value !== null && value !== undefined && String(value).trim().length > 0;
};

const serialiseValue = (dataPoint, value) => {
  switch (dataPoint?.valueType) {
    case 'multi':
      return (value || []).map((option) => option?.value ?? option);
    case 'options':
      return value?.value ?? value;
    case 'boolean':
      return value?.value ?? value;
    case 'number':
      return dataPoint.cast === 'number' ? Number(value) : String(value);
    default:
      return value;
  }
};

/** Turns the panel rows into the `advancedFilters` array the search API expects. */
export const buildAdvancedFilters = (rows) =>
  (rows || []).filter(isRowComplete).map((row) => ({
    condition: {
      field: row.field,
      operator: row.operator,
      value: serialiseValue(getDataPoint(row.field), row.value),
    },
    join: row.join || 'AND',
  }));
